import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { change, initialize, reset } from 'redux-form';
import { DiscountFormFields } from 'pages/Library/Discount/NewDiscountDetail/DiscountForm/CustomDiscountForm';
import isNil from 'lodash/isNil';
import { unleashHasFeature } from 'mf-shared/utils/featureFlags';
import { ListDiscounts200Response } from '@ce-openapi/moka-inventory-discount-service-ts-axios-client';
import {
  customDiscountsRequest,
  discountsRequest,
  pageRequest,
  setCustomDiscounts,
  setCustomDiscountsCursor,
  submitDiscountOutletFormCreate,
} from '../../actions/library/discount';
import * as act from '../../actions';
import * as actNotif from '../../actions/notifications';
import * as actDiscount from '../../actions/library/discount';
import * as actOutletList from '../../actions/outletPages/list';
import {
  LIBRARY_DISCOUNT_ASK_DELETE_DISCOUNT,
  LIBRARY_DISCOUNT_CANCEL_DELETE_DISCOUNT,
  LIBRARY_DISCOUNT_CLEAR_AMOUNT_FIELD,
  LIBRARY_DISCOUNT_DELETE_DISCOUNT,
  LIBRARY_DISCOUNT_ON_CHANGE_OUTLET_IDS,
  LIBRARY_DISCOUNT_ON_CLICK_ASSIGN_DISCOUNT_ASSIGN_OUTLET_MODAL,
  LIBRARY_DISCOUNT_ON_CLICK_ASSIGN_OUTLET,
  LIBRARY_DISCOUNT_ON_CLICK_CANCEL,
  LIBRARY_DISCOUNT_ON_CLICK_CANCEL_ASSIGN_OUTLET_MODAL,
  LIBRARY_DISCOUNT_ON_CLICK_CREATE_DISCOUNT,
  LIBRARY_DISCOUNT_ON_CLICK_ROW,
  LIBRARY_DISCOUNT_OUTLET_DELETE_DISCOUNT,
  LIBRARY_DISCOUNT_OUTLET_ON_CLICK_VIEW_MORE_OUTLETS,
  LIBRARY_DISCOUNT_OUTLET_SUBMIT_FORM_UPDATE,
  LIBRARY_DISCOUNT_SEARCH_OUTLET,
  LIBRARY_DISCOUNT_SELECT_ALL_OUTLETS,
  LIBRARY_DISCOUNT_SUBMIT_FORM_CREATE,
  LIBRARY_DISCOUNT_SUBMIT_FORM_CREATE_ALL_OUTLET,
  LIBRARY_DISCOUNT_SUBMIT_FORM_UPDATE,
  LIBRARY_DISCOUNT_UNSELECT_ALL_OUTLETS,
} from '../../actions/library/discount/constants';
import { setSelectedOutletIds } from '../../actions/actionBar';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import { Discount, getCustomDiscountsApi } from '../../api/libraries/discounts';
import * as apiOutlets from '../../api/outlets';
import * as apiDiscount from '../../api/libraries/discounts';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { useMokaOutletPropsFirst } from '../../utils/formatter';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapLibraryDiscount from '../../clevertap/library/discount';

type DiscountResponse = Awaited<ReturnType<typeof apiDiscount.getDiscounts>>;

const getCloneNotification = (state) =>
  state.outletPages.list.showCloneOutletNotification.notification;

const defaultPerPageDiscountsSize = 30;

function getOutletDetailFromDiscount(outlets, detailDiscount) {
  return outlets.map((out) => {
    let checkedOutlet = false;
    if (detailDiscount.outlet_ids.find((outlet) => outlet === out.id)) {
      checkedOutlet = true;
    }
    return {
      id: out.id,
      name: useMokaOutletPropsFirst(out, (o) => o.name),
      checked: checkedOutlet,
      show: true,
    };
  });
}

function* getRegularDiscounts(
  params: Parameters<typeof apiDiscount.getDiscounts>[0],
  pageNo: number,
  transform?: (discounts: Discount[]) => Discount[]
) {
  const response: DiscountResponse = yield call(
    apiDiscount.getDiscounts,
    params
  );
  const discounts = response.data.results;
  yield put(
    actDiscount.setDiscounts(transform ? transform(discounts) : discounts)
  );
  yield put(actDiscount.setTotalPage(response.data.total_pages));
  yield put(actDiscount.setCurrentPage(pageNo));
}

function* getCustomDiscount(
  params: Parameters<typeof getCustomDiscountsApi>[0],
  transform?: (
    discounts: ListDiscounts200Response['results']
  ) => ListDiscounts200Response['results']
) {
  const {
    results,
    ...rest
  }: Awaited<ReturnType<typeof apiDiscount.getCustomDiscountsApi>> = yield call(
    apiDiscount.getCustomDiscountsApi,
    params
  );
  const discounts = transform ? transform(results) : results;
  yield put(setCustomDiscounts(discounts));
  yield put(setCustomDiscountsCursor(rest));
}

function* backToList(
  outletIds: string[] | undefined,
  transform?: (discounts: Discount[]) => Discount[]
) {
  const { isEnabled: enableCustomDiscount } = yield select(
    unleashHasFeature('custom-discount-backoffice')
  );
  yield enableCustomDiscount
    ? call(getCustomDiscount, {
        outletIds,
        perPage: defaultPerPageDiscountsSize,
      })
    : call(
        getRegularDiscounts,
        {
          page: 1,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          per_page: defaultPerPageDiscountsSize,
          outletIds,
        },
        1,
        transform
      );
  yield put(actOutletList.checkCloningOutletOnProgress());
  yield put(reset('discountAssignOutlet'));
  yield put(actDiscount.unSetDiscountDetail());
}

export function* fillForm(discount) {
  const formName = 'discount';
  const data: DiscountFormFields & { id: string } = {
    id: discount.id,
    name: discount.name,
    amount: discount.amount ? discount.amount.toString() : discount.amount,
    typeAmount: isNil(discount.amount) ? null : discount.type === 'percentage',
    category: isNil(discount.amount) ? 'customAmount' : 'fixedAmount',
    discountTypeCustom: isNil(discount.amount) ? discount.type : null,
  };
  yield put(initialize(formName, data));
  yield put(reset('discountAssignOutlet'));
}

export function* resetForm() {
  yield put(
    initialize('discount', {
      id: null,
      name: null,
      amount: null,
      typeAmount: null,
      category: null,
      discountTypeCustom: null,
    })
  );
  yield put(reset('discountAssignOutlet'));
}

function* getCloneOutletNotification() {
  return yield select(getCloneNotification);
}

export function* pageRequestSaga() {
  // reset...
  yield put(actDiscount.setDiscounts([]));
  yield put(showPageLoadingSpinner(true));
  yield put(actDiscount.unSetDiscountDetail());
  yield put(actDiscount.setOutlets([]));
  yield put(act.showProgressBar());
  try {
    const { data: outlets } = yield call(apiOutlets.outlets);
    yield put(actDiscount.setOutlets(outlets));

    const localStorageIds = getSelectedOutletIdFromLocalStorage();
    // either 1 outlet or all outlets are selected
    const outletIds =
      localStorageIds?.length === 1
        ? localStorageIds
        : outlets.map((r) => r.id);

    yield put(initialize('search', { outletIds }));
    clevertapLibraryDiscount.libraryDiscountStart(outletIds);

    yield call(backToList, outletIds);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* discountsRequestSaga({
  payload,
}: ReturnType<typeof discountsRequest>) {
  const { params, previousDiscounts } = payload;
  yield put(act.showProgressBar());
  try {
    yield call(
      getRegularDiscounts,
      {
        ...params,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        per_page: defaultPerPageDiscountsSize,
      },
      params.page,
      (discounts) => previousDiscounts.concat(discounts)
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* customDiscountsRequestSaga({
  payload,
}: ReturnType<typeof customDiscountsRequest>) {
  const { params, previousDiscounts } = payload;
  yield put(act.showProgressBar());
  try {
    yield call(
      getCustomDiscount,
      {
        ...params,
        perPage: defaultPerPageDiscountsSize,
      },
      (discounts) => previousDiscounts.concat(discounts)
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* unSetDetailsDiscount() {
  try {
    yield put(act.showProgressBar());
    yield put(actDiscount.unSetDiscountDetail());
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onChangeOutletIds(action) {
  yield put(actDiscount.setDiscounts([]));
  yield put(showPageLoadingSpinner(true));

  const {
    payload: { outletIds },
  } = action;

  try {
    yield put(act.showProgressBar());

    setSelectedOutletIdToLocalStorage(outletIds);
    yield put(setSelectedOutletIds(outletIds));
    yield put(change('search', 'outletIds', outletIds));

    const { isEnabled: enableCustomDiscount } = yield select(
      unleashHasFeature('custom-discount-backoffice')
    );
    yield enableCustomDiscount
      ? call(getCustomDiscount, {
          outletIds,
          perPage: defaultPerPageDiscountsSize,
        })
      : call(
          getRegularDiscounts,
          {
            page: 1,
            outletIds,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            per_page: defaultPerPageDiscountsSize,
          },
          1
        );

    yield call(resetForm);
    yield put(actOutletList.checkCloningOutletOnProgress());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield call(unSetDetailsDiscount);
    yield put(act.hideProgressBar());
  }
  yield put(showPageLoadingSpinner(false));
}

export function* setDetailsDiscount(action) {
  const {
    payload: { discount, outlets },
  } = action;

  const param = {
    name: discount.name,
    type: discount.type,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    business_id: discount.business_id,
    amount: discount.amount,
  };

  const res = yield call(apiDiscount.getDiscountOutletsDetail, param);
  const detailDiscount = res.data;
  const detailOutlets = getOutletDetailFromDiscount(outlets, detailDiscount);
  const checkedOutlets = detailOutlets.filter((outlet) => outlet.checked);
  yield put(act.showProgressBar());
  // tahan bbrp ms karena component harus dirender dlu supaya gak muncul setengah isi
  // initialize form discountAssignOutlet and selectedOutlets
  yield put(
    initialize('discountAssignOutlet', { selectedOutlets: detailOutlets })
  );
  if (checkedOutlets.length > 10) {
    const partialOutlets = checkedOutlets.slice(0, 10);
    yield put(actDiscount.setPartialOutlets(partialOutlets));
    yield put(actDiscount.showViewMoreOutletsButton());
  } else {
    yield put(actDiscount.setPartialOutlets(checkedOutlets));
    yield put(actDiscount.hideViewMoreOutletsButton());
  }
  yield put(actDiscount.setSelectedOutlets(detailOutlets));
  yield put(actDiscount.setDiscountDetail(detailDiscount));
  yield call(delay, 750);
  yield call(fillForm, discount);
  yield put(act.hideProgressBar());
  yield put(actDiscount.hideErrorDuplicateDiscountName());
  yield put(actDiscount.hideErrorOutletNotAssigned());
  yield put(actDiscount.hideErrorDuplicateDiscountName());
}

export function* onClickCreateDiscount(request) {
  const {
    payload: { outlets },
  } = request;

  const form = {
    selectedOutlets: outlets.map((outlet) => {
      return {
        id: outlet.id,
        name: useMokaOutletPropsFirst(outlet, (o) => o.name),
        checked: false,
        show: true,
      };
    }),
  };
  yield put(initialize('discountAssignOutlet', form));
  const selectedOutlets = outlets.map((outlet) => {
    return { id: outlet.id, name: outlet.name, checked: false, show: true };
  });

  yield put(actDiscount.hideErrorDuplicateDiscountName());
  yield put(actDiscount.setSelectedOutlets(selectedOutlets));
  yield put(actDiscount.setPartialOutlets(selectedOutlets));

  try {
    yield put(reset('discount'));
    yield put(act.showProgressBar());
    yield put(actDiscount.setNewDiscount());
    // tahan bbrp ms karena component harus dirender dlu supaya gak muncul setengah isi
    yield call(delay, 750);
    yield call(resetForm);
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onChangeSwitchField() {
  const formName = 'discount';
  yield put(change(formName, 'amount', ''));
}

export function* submitFormCreate(request) {
  const {
    payload: { data, previousDiscounts },
  } = request;
  yield put(act.showProgressBar());
  yield put(actDiscount.isSubmittingForm(true));

  try {
    // @ts-expect-error
    const response = yield call(apiDiscount.postDiscount, data);
    // set to reducers
    const discount = response.data;
    const discounts = [discount];
    previousDiscounts.forEach((g) => discounts.push(g));

    yield put(
      actNotif.success(
        'Discount successfully created.',
        yield getCloneOutletNotification()
      )
    );
    yield put(actDiscount.setDiscounts(discounts));
    yield put(actDiscount.setDiscountDetail(discount));
    yield call(fillForm, discount);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(actDiscount.isSubmittingForm(false));
  }
}

export function* submitFormCreateAllOutlet(request) {
  const {
    payload: { data, previousDiscounts },
  } = request;

  yield put(act.showProgressBar());
  yield put(actDiscount.isSubmittingForm(true));
  try {
    // @ts-expect-error
    const response = yield call(apiDiscount.postCreateAllOutlet, data);
    // set to reducers
    const discount = response.data;
    const discounts = [discount];
    previousDiscounts.forEach((g) => discounts.push(g));

    yield put(
      actNotif.success(
        'Discount successfully created.',
        yield getCloneOutletNotification()
      )
    );
    yield put(actDiscount.setDiscounts(discounts));
    yield put(actDiscount.setDiscountDetail(discount));
    yield call(fillForm, discount);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(actDiscount.isSubmittingForm(false));
  }
}

export function* submitFormUpdate(request) {
  const {
    payload: { data, discountId, previousDiscounts },
  } = request;
  yield put(act.showProgressBar());
  yield put(actDiscount.isSubmittingForm(true));
  try {
    // @ts-expect-error
    const response = yield call(apiDiscount.putDiscount, discountId, data);
    // set to reducers
    const discount = response.data;
    const discounts: any[] = [];
    previousDiscounts.forEach((g) => {
      if (g.id === discountId) {
        discounts.push(discount);
      } else {
        discounts.push(g);
      }
    });

    yield put(
      actNotif.success(
        'Discount successfully updated.',
        yield getCloneOutletNotification()
      )
    );
    yield put(actDiscount.setDiscounts(discounts));
    yield put(actDiscount.setDiscountDetail(discount));
    yield call(fillForm, discount);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(actDiscount.isSubmittingForm(false));
  }
}

export function* deleteDiscount(request) {
  const {
    payload: { discountId, previousDiscounts },
  } = request;
  yield put(act.showProgressBar());
  try {
    // @ts-expect-error
    yield call(apiDiscount.deleteDiscount, discountId, previousDiscounts.name);
    // set to reducers
    const discounts: any[] = [];
    previousDiscounts.forEach((discount) => {
      if (discount.id !== discountId) {
        discounts.push(discount);
      }
    });
    yield put(actDiscount.setDiscounts(discounts));
    yield put(actDiscount.setDeletedDiscount(null));
    yield put(actDiscount.hideDeleteDiscountModal());
    yield call(unSetDetailsDiscount);
    yield call(resetForm);
    yield put(
      actNotif.success(
        'Discount successfully deleted.',
        yield getCloneOutletNotification()
      )
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* askDeleteDiscount(request) {
  const {
    payload: { deletedDiscount },
  } = request;
  yield put(actDiscount.setDeletedDiscount(deletedDiscount));
  yield put(actDiscount.showDeleteDiscountModal());
}

export function* cancelDeleteDiscount() {
  yield put(actDiscount.setDeletedDiscount(null));
  yield put(actDiscount.hideDeleteDiscountModal());
}

export function* onClickCancel() {
  yield put(actDiscount.unSetDiscountDetail());
  yield call(resetForm);
}

export function* clickAssignOutlet() {
  yield put(actDiscount.showDiscountAssignOutletModal());
}

export function* clickCancelModal(request) {
  const {
    payload: { previousSelectedOutlets },
  } = request;
  yield put(actDiscount.hideDiscountAssignOutletModal());
  for (let idx = 0; idx < previousSelectedOutlets.length; idx++) {
    if (previousSelectedOutlets.length > 0 && previousSelectedOutlets[idx]) {
      yield put(
        change(
          'discountAssignOutlet',
          `selectedOutlets[${idx}].checked`,
          previousSelectedOutlets[idx].checked
        )
      );
    }
    yield put(
      change('discountAssignOutlet', `selectedOutlets[${idx}].show`, true)
    );
  }
}

export function* searchOutletOnModal(request) {
  const {
    payload: { outlets, searchKeyword },
  } = request;
  for (let idx = 0; idx < outlets.length; idx++) {
    const res = outlets[idx].name.search(new RegExp(searchKeyword, 'i'));
    yield put(
      change('discountAssignOutlet', `selectedOutlets[${idx}].show`, res !== -1)
    );
  }
}

export function* selectAllOutletsModal(request) {
  const {
    payload: { allOutletsForm },
  } = request;
  const { selectedOutlets } = allOutletsForm;
  for (let idx = 0; idx < selectedOutlets.length; idx++) {
    if (selectedOutlets[idx].show) {
      yield put(
        change('discountAssignOutlet', `selectedOutlets[${idx}].checked`, true)
      );
    }
  }
}

export function* unselectAllOutletsModal(request) {
  const {
    payload: { allOutletsForm },
  } = request;
  const { selectedOutlets } = allOutletsForm;
  for (let idx = 0; idx < selectedOutlets.length; idx++) {
    if (selectedOutlets[idx].show) {
      yield put(
        change('discountAssignOutlet', `selectedOutlets[${idx}].checked`, false)
      );
    }
  }
}

export function* confirmAssignOutlet(request) {
  const {
    payload: {
      assignedOutlets: { selectedOutlets },
    },
  } = request;
  yield put(actDiscount.setSelectedOutlets(selectedOutlets));
  yield put(actDiscount.setPartialOutlets(selectedOutlets));
  yield put(actDiscount.hideDiscountAssignOutletModal());
}

export function* submitCreateDiscountForm({
  payload,
}: ReturnType<typeof submitDiscountOutletFormCreate>) {
  const { data, email, phone, selectedOutletIds } = payload;
  yield put(act.showProgressBar());
  yield put(actDiscount.isSubmittingForm(true));
  try {
    yield call(apiDiscount.postCreateDiscountWithOutlets, data);
    yield put(
      actNotif.success(
        'Discount successfully created.',
        yield getCloneOutletNotification()
      )
    );
    clevertapLibraryDiscount.backofficeManageDiscountCreate({
      discount: data,
    });
    yield call(backToList, selectedOutletIds, (discounts) =>
      discounts.map((d) => {
        // Optimistically update the outlets in when creating for multiple outlets in case of server lag
        // Likely to have lag if the number of outlets is less than what was posted
        if (d.name === data.name && data.outlet_ids.length > d.total_outlets) {
          return {
            ...d,
            total_outlets: data.outlet_ids.length,
            outlet_ids: data.outlet_ids,
          };
        }
        return d;
      })
    );
  } catch (error) {
    assertErrorHasMessage(error);
    if (
      error.message ===
      'Discount name already exists in your business, please use another name.'
    ) {
      yield put(actDiscount.showErrorDuplicateDiscountName());
      clevertapLibraryDiscount.backofficeManageDiscountNameError(
        data.name,
        data.business_id,
        'create',
        email,
        phone
      );
    }
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(actDiscount.isSubmittingForm(false));
  }
}

export function* updateDiscountOutlet(request) {
  const {
    payload: { data, previousDiscountDetail, email, phone, selectedOutletIds },
  } = request;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const deleted_outlet_ids = previousDiscountDetail.outlet_ids.filter(
    // eslint-disable-next-line @typescript-eslint/naming-convention
    (outlet_id) => !data.outlet_ids.includes(outlet_id)
  );

  const body = {
    name: data.name,
    amount: data.amount,
    type: data.type,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    original_name: previousDiscountDetail.name,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    original_amount: previousDiscountDetail.amount,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    original_type: previousDiscountDetail.type,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    updated_outlet_ids: data.outlet_ids,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    deleted_outlet_ids,
  };

  yield put(act.showProgressBar());
  try {
    yield call(apiDiscount.updateDiscountWithOutlets, body);
    clevertapLibraryDiscount.backofficeManageDiscountUpdate({
      discount: body,
      oldDiscount: previousDiscountDetail,
    });
    yield put(
      actNotif.success(
        'Discount successfully updated.',
        yield getCloneOutletNotification()
      )
    );
    yield call(backToList, selectedOutletIds);
  } catch (error) {
    assertErrorHasMessage(error);
    if (
      error.message ===
      'Discount name already exists in your business, please use another name.'
    ) {
      yield put(actDiscount.showErrorDuplicateDiscountName());
      clevertapLibraryDiscount.backofficeManageDiscountNameError(
        data.name,
        previousDiscountDetail.business_id,
        'update',
        email,
        phone
      );
    }
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* deleteDiscountOutlet(request) {
  const {
    payload: { data, selectedOutletIds },
  } = request;
  const body: { name: string; amount: number; type: string } = {
    name: data.name,
    amount: data.amount,
    type: data.type,
  };

  yield put(act.showProgressBar());
  try {
    yield call(apiDiscount.deleteDiscountWithOutlets, body);
    yield put(
      actNotif.success(
        'Discount successfully deleted.',
        yield getCloneOutletNotification()
      )
    );
    clevertapLibraryDiscount.backofficeManageDiscountDelete({
      discount: data,
    });
    yield put(actDiscount.setDeletedDiscount(null));
    yield put(actDiscount.hideDeleteDiscountModal());
    yield call(unSetDetailsDiscount);
    yield call(resetForm);
    yield put(
      actNotif.success(
        'Discount successfully deleted.',
        yield getCloneOutletNotification()
      )
    );
    yield call(backToList, selectedOutletIds, (discounts) =>
      // Optimistically update the UI here in case the deleted item is still there.
      // Deleted item takes some time to be cleared in backend
      discounts.filter((d) =>
        Object.entries(body).some(([key, value]) => d[key] !== value)
      )
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onClickViewMore(action) {
  const {
    payload: { currentIndex, detailDiscount, outlets },
  } = action;

  const detailOutlets = getOutletDetailFromDiscount(outlets, detailDiscount);
  const checkedOutlets = detailOutlets.filter((outlet) => outlet.checked);

  const partialOutlets = checkedOutlets.slice(0, currentIndex + 10);

  actDiscount.hideViewMoreOutletsButton();
  yield put(actDiscount.setPartialOutlets(partialOutlets));
}

export default function* saga() {
  yield takeLatest(pageRequest, pageRequestSaga);
  yield takeLatest(LIBRARY_DISCOUNT_ON_CLICK_ROW, setDetailsDiscount);
  yield takeLatest(LIBRARY_DISCOUNT_ON_CHANGE_OUTLET_IDS, onChangeOutletIds);
  yield takeLatest(
    LIBRARY_DISCOUNT_ON_CLICK_CREATE_DISCOUNT,
    onClickCreateDiscount
  );
  yield takeLatest(LIBRARY_DISCOUNT_CLEAR_AMOUNT_FIELD, onChangeSwitchField);
  yield takeEvery(discountsRequest, discountsRequestSaga);
  yield takeEvery(customDiscountsRequest, customDiscountsRequestSaga);
  yield takeLatest(LIBRARY_DISCOUNT_SUBMIT_FORM_CREATE, submitFormCreate);
  yield takeLatest(LIBRARY_DISCOUNT_SUBMIT_FORM_UPDATE, submitFormUpdate);
  yield takeLatest(
    LIBRARY_DISCOUNT_SUBMIT_FORM_CREATE_ALL_OUTLET,
    submitFormCreateAllOutlet
  );
  yield takeLatest(LIBRARY_DISCOUNT_ASK_DELETE_DISCOUNT, askDeleteDiscount);
  yield takeLatest(LIBRARY_DISCOUNT_DELETE_DISCOUNT, deleteDiscount);
  yield takeLatest(
    LIBRARY_DISCOUNT_CANCEL_DELETE_DISCOUNT,
    cancelDeleteDiscount
  );
  yield takeLatest(LIBRARY_DISCOUNT_ON_CLICK_CANCEL, onClickCancel);
  yield takeLatest(LIBRARY_DISCOUNT_ON_CLICK_ASSIGN_OUTLET, clickAssignOutlet);
  yield takeLatest(
    LIBRARY_DISCOUNT_ON_CLICK_CANCEL_ASSIGN_OUTLET_MODAL,
    clickCancelModal
  );
  yield takeLatest(LIBRARY_DISCOUNT_SEARCH_OUTLET, searchOutletOnModal);
  yield takeLatest(LIBRARY_DISCOUNT_SELECT_ALL_OUTLETS, selectAllOutletsModal);
  yield takeLatest(
    LIBRARY_DISCOUNT_UNSELECT_ALL_OUTLETS,
    unselectAllOutletsModal
  );
  yield takeLatest(
    LIBRARY_DISCOUNT_ON_CLICK_ASSIGN_DISCOUNT_ASSIGN_OUTLET_MODAL,
    confirmAssignOutlet
  );
  yield takeLatest(submitDiscountOutletFormCreate, submitCreateDiscountForm);
  yield takeLatest(
    LIBRARY_DISCOUNT_OUTLET_SUBMIT_FORM_UPDATE,
    updateDiscountOutlet
  );
  yield takeLatest(
    LIBRARY_DISCOUNT_OUTLET_DELETE_DISCOUNT,
    deleteDiscountOutlet
  );
  yield takeLatest(
    LIBRARY_DISCOUNT_OUTLET_ON_CLICK_VIEW_MORE_OUTLETS,
    onClickViewMore
  );
}
